import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  TablePagination,
  Box,
  TextField,
  InputAdornment,
  useMediaQuery,
  Checkbox,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { useTheme } from "@mui/material/styles";
import CustomToggle from "../../Base/views/CustomToggle";

const CustomTable = ({
  data = [],
  loading = false,
  columns = [],
  searchTerm = "",
  onSearchChange = () => {},
  page = 0,
  rowsPerPage = 25,
  onPageChange = () => {},
  onRowsPerPageChange = () => {},
  totalCount = 0,
  inactive = false,
  onToggleInactive = () => {},
  showGlobalSearch = true,
  showInactiveToggle = false,
  onColumnReorder = () => {},
  showColumnSelector = false,
  showRowSelection = false,
  onSelectionChange = () => {},
  bulkActions = [], // New prop for bulk actions
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  // Sorting state
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Drag and drop state
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [reorderedColumns, setReorderedColumns] = useState([...columns]);

  // Column selection state
  const [visibleColumns, setVisibleColumns] = useState(
    columns.reduce((acc, col, index) => {
      acc[index] = true; // All columns visible by default
      return acc;
    }, {})
  );

  // Row selection state
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectAll, setSelectAll] = useState(false);

  // Column selector popover state
  const [columnSelectorAnchor, setColumnSelectorAnchor] = useState(null);

  // Update reorderedColumns when columns prop changes
  React.useEffect(() => {
    setReorderedColumns([...columns]);
    // Reset visible columns when columns prop changes
    setVisibleColumns(
      columns.reduce((acc, col, index) => {
        acc[index] = true;
        return acc;
      }, {})
    );
  }, [columns]);

  // Reset selection when data changes
  React.useEffect(() => {
    setSelectedRows(new Set());
    setSelectAll(false);
  }, [data, page, rowsPerPage]);

  // Handle drag start
  const handleDragStart = (e, columnIndex) => {
    setDraggedColumn(columnIndex);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", columnIndex);
  };

  // Handle drag over
  const handleDragOver = (e, columnIndex) => {
    e.preventDefault();
    if (draggedColumn !== columnIndex) {
      setDragOverColumn(columnIndex);
    }
  };

  // Handle drag leave
  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  // Handle drop
  const handleDrop = (e, targetIndex) => {
    e.preventDefault();
    if (draggedColumn === null || draggedColumn === targetIndex) return;

    const newColumns = [...reorderedColumns];
    const [movedColumn] = newColumns.splice(draggedColumn, 1);
    newColumns.splice(targetIndex, 0, movedColumn);

    setReorderedColumns(newColumns);
    setDraggedColumn(null);
    setDragOverColumn(null);
    
    // Call the callback with new column order
    onColumnReorder(newColumns);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  // Handle sorting
  const handleSort = (columnKey, dataType = 'string') => {
    let direction = 'asc';
    
    if (sortConfig.key === columnKey && sortConfig.direction === 'asc') {
      direction = 'desc';
    } else if (sortConfig.key === columnKey && sortConfig.direction === 'desc') {
      // Reset to no sorting if clicking the same column third time
      setSortConfig({ key: null, direction: 'asc' });
      return;
    }
    
    setSortConfig({ key: columnKey, direction, dataType });
  };

  // Toggle column visibility
  const toggleColumnVisibility = (columnIndex) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnIndex]: !prev[columnIndex]
    }));
  };

  // Handle individual row selection
  const handleRowSelect = (rowIndex, rowData) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowIndex)) {
      newSelected.delete(rowIndex);
    } else {
      newSelected.add(rowIndex);
    }
    setSelectedRows(newSelected);
    
    // Get selected row data
    const selectedData = Array.from(newSelected).map(index => paginatedData[index]);
    onSelectionChange(Array.from(newSelected), selectedData);
  };

  // Handle select all
  const handleSelectAll = () => {
    if (selectAll || selectedRows.size > 0) {
      setSelectedRows(new Set());
      setSelectAll(false);
      onSelectionChange([], []);
    } else {
      const allRowIndices = new Set(paginatedData.map((_, index) => index));
      setSelectedRows(allRowIndices);
      setSelectAll(true);
      onSelectionChange(Array.from(allRowIndices), paginatedData);
    }
  };

  // Sort data based on sortConfig - PERFORMANCE OPTIMIZED
  const sortedAndFilteredData = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    
    let filtered = data;
    
    // Apply search filter only if searchTerm exists
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = data.filter(item => {
        return reorderedColumns.some(col => {
          const value = item[col.field];
          return value && value.toString().toLowerCase().includes(searchLower);
        });
      });
    }

    // Apply sorting only if sortConfig exists
    if (sortConfig.key) {
      filtered = [...filtered]; // Create a copy to avoid mutating original
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle null/undefined values
        if (aValue == null) aValue = '';
        if (bValue == null) bValue = '';

        // Handle different data types
        if (sortConfig.dataType === 'date') {
          aValue = new Date(aValue);
          bValue = new Date(bValue);
        } else if (sortConfig.dataType === 'number') {
          aValue = Number(aValue);
          bValue = Number(bValue);
        } else {
          // Default to string comparison
          aValue = aValue.toString().toLowerCase();
          bValue = bValue.toString().toLowerCase();
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [data, searchTerm, sortConfig, reorderedColumns]);

  // Paginated data - PERFORMANCE: Only slice what's needed for display
  const paginatedData = useMemo(() => {
    return sortedAndFilteredData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) || [];
  }, [sortedAndFilteredData, page, rowsPerPage]);

  // Clear all filters (only search in this case)
  const clearAllFilters = () => {
    setSortConfig({ key: null, direction: 'asc' });
    onSearchChange("");
  };

  const clearSearch = () => {
    onSearchChange("");
  };

  // Check if any filters are active
  const hasActiveFilters = searchTerm || sortConfig.key;

  // Get sort icon for a column
  const getSortIcon = (columnKey) => {
    if (sortConfig.key !== columnKey) {
      return null; // No sort icon when not sorted by this column
    }
    
    return sortConfig.direction === 'asc' ? 
      <ArrowUpwardIcon fontSize="small" /> : 
      <ArrowDownwardIcon fontSize="small" />;
  };

  // Filter columns based on visibility
  const filteredColumns = reorderedColumns.filter((_, index) => visibleColumns[index]);

  // Calculate dynamic column widths based on screen size
  const getColumnWidth = (column) => {
    if (column.width) return column.width;
    
    if (isMobile) {
      return column.mobileWidth || '120px';
    } else if (isTablet) {
      return column.tabletWidth || '150px';
    } else {
      return column.desktopWidth || '180px';
    }
  };

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2, mb: 4 }} className="meepl-bgcolor">
      {/* Toolbar with Search and Toggle */}
      <Box 
        className="meepl-bgcolor"
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 1,
          borderBottom: `1px solid #d1c4e9`,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        {/* Left Side - Search */}
        <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, maxWidth: 400, minWidth: 250 }}>
          {showGlobalSearch && (
            <TextField
              fullWidth
              size="small"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              variant="standard"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={clearSearch}
                      sx={{ padding: '4px' }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
                disableUnderline: true,
              }}
              sx={{
                '& .MuiInput-root': {
                  padding: '2px 6px',
                }
              }}
            />
          )}
        </Box>

        {/* Right Side - Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          
          {hasActiveFilters && (
            <Tooltip title="Clear all filters and sorting">
              <IconButton size="small" onClick={clearAllFilters} color="primary">
                <ClearIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}

          {/* Selection Info - Inside the toolbar with toggle background */}
          {showRowSelection && selectedRows.size > 0 && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              backgroundColor: 'rgba(243, 237, 253, 0.8)',
              borderRadius: 1,
              px: 1.5,
              py: 0.5,
            }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: '#8b44ff' }}>
                {selectedRows.size} workspace(s) selected
              </Typography>
              {/* Bulk Actions */}
              {bulkActions.map((action, index) => (
                <Tooltip key={index} title={action.tooltip}>
                  <IconButton
                    size="small"
                    onClick={() => action.onClick(Array.from(selectedRows), paginatedData.filter((_, idx) => selectedRows.has(idx)))}
                    sx={{
                      color: action.color || '#8b44ff',
                      '&:hover': {
                        backgroundColor: 'rgba(139, 68, 255, 0.1)',
                      }
                    }}
                  >
                    {action.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          )}
          
          {showInactiveToggle && (
            <CustomToggle
              isChecked={inactive}
              setIsChecked={onToggleInactive}
              handleToggleChange={onToggleInactive}
              Yestext="Inactive"
              NoText="Active"
              style={{
                height: "32px",
                padding: "0px",
                width: "145px",
                labelPadding: "1px",
              }}
            />
          )}

          {/* Column Selector Button - After Toggle */}
          {showColumnSelector && (
            <Tooltip title="Select Columns">
              <IconButton 
                size="small" 
                onClick={(e) => setColumnSelectorAnchor(e.currentTarget)}
                sx={{
                  '&:hover': {
                    backgroundColor: 'transparent',
                  }
                }}
              >
                <ViewColumnIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Table Container with Horizontal Scroll */}
      <Box sx={{ position: 'relative', minHeight: '55vh', maxHeight: '55vh' }}>
        {(loading || paginatedData.length === 0) ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '55vh',
              maxHeight: '55vh',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              {loading ? 'Loading...' : (hasActiveFilters ? 'No matching records found' : 'No data available')}
            </Typography>
          </Box>
        ) : (
          <TableContainer 
            sx={{ 
              minHeight: '55vh',
              maxHeight: '55vh',
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                height: '8px',
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#c1c1c1',
                borderRadius: '4px',
                '&:hover': {
                  background: '#a8a8a8',
                },
              },
            }}
          >
            <Table 
              stickyHeader 
              size="small" 
              className="meepl-bgcolor"
              sx={{
                minWidth: 'max-content',
                tableLayout: 'auto', // Changed to auto for better text wrapping
              }}
            >
              <TableHead>
                <TableRow>
                  {/* Data Columns with integrated checkbox in first column */}
                  {filteredColumns.map((column, index) => (
                    <TableCell 
                      key={index} 
                      className="MuiTableCell-head"
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      sx={{ 
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        padding: "8px 12px", // Increased padding for better text wrapping
                        borderBottom: `2px solid #d1c4e9`,
                        backgroundColor: dragOverColumn === index ? 'action.hover' : 'inherit',
                        cursor: 'grab',
                        position: 'relative',
                        minWidth: getColumnWidth(column),
                        maxWidth: column.maxWidth || '400px', // Increased max width
                        whiteSpace: 'normal', // Changed to normal for word wrap
                        wordWrap: 'break-word', // Added word wrap
                        overflow: 'visible', // Changed to visible to show full text
                        textOverflow: 'unset', // Remove ellipsis
                        '&:active': {
                          cursor: 'grabbing',
                        },
                        transition: 'background-color 0.2s ease',
                      }}
                    >
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', // Changed to flex-start for better alignment
                          gap: 1,
                        }}
                      >
                        {/* Checkbox in first column header */}
                        {index === 0 && showRowSelection && (
                          <Checkbox
                            checked={selectAll}
                            indeterminate={selectedRows.size > 0 && selectedRows.size < paginatedData.length}
                            onChange={handleSelectAll}
                            size="small"
                            sx={{
                              padding: '2px',
                              flexShrink: 0,
                              mt: '2px', // Align with text
                            }}
                          />
                        )}
                        
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
                          <Box 
                            sx={{ 
                              flex: 1,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'flex-start',
                              justifyContent: 'space-between',
                              minWidth: 0,
                            }}
                            onClick={() => handleSort(column.field, column.dataType)}
                          >
                            <Typography 
                              variant="body2" 
                              sx={{
                                fontWeight: 600,
                                whiteSpace: 'normal', // Changed to normal
                                wordWrap: 'break-word', // Added word wrap
                                overflow: 'visible', // Show full text
                                textOverflow: 'unset', // Remove ellipsis
                                flex: 1,
                                lineHeight: 1.4, // Better line height for wrapped text
                              }}
                            >
                              {column.label}
                            </Typography>
                            <IconButton
                              size="small"
                              sx={{
                                padding: '2px',
                                opacity: sortConfig.key === column.field ? 1 : 0.3,
                                color: sortConfig.key === column.field ? '#8b44ff' : 'inherit',
                                flexShrink: 0,
                                mt: '-2px', // Better alignment
                                '&:hover': {
                                  opacity: 0.7,
                                  backgroundColor: 'transparent',
                                },
                              }}
                            >
                              {getSortIcon(column.field) || <ArrowUpwardIcon fontSize="small" />}
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((row, rowIndex) => (
                  <TableRow key={rowIndex} hover>
                    {/* Data Cells with integrated checkbox in first column */}
                    {filteredColumns.map((column, colIndex) => (
                      <TableCell 
                        key={colIndex} 
                        sx={{ 
                          padding: "8px 12px", // Increased padding
                          whiteSpace: 'normal',
                          wordWrap: 'break-word',
                          overflow: 'visible',
                          textOverflow: 'unset',
                          lineHeight: 1.4,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                          {/* Checkbox in first column */}
                          {colIndex === 0 && showRowSelection && (
                            <Checkbox
                              checked={selectedRows.has(rowIndex)}
                              onChange={() => handleRowSelect(rowIndex, row)}
                              size="small"
                              sx={{
                                padding: '2px',
                                mt: '2px',
                                flexShrink: 0,
                              }}
                            />
                          )}
                          
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            {column.render ? column.render(row) : (
                              <Typography 
                                variant="body2" 
                                sx={{
                                  whiteSpace: 'normal',
                                  wordWrap: 'break-word',
                                  overflow: 'visible',
                                  textOverflow: 'unset',
                                  lineHeight: 1.4,
                                }}
                              >
                                {row[column.field]}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
      
      <TablePagination
        rowsPerPageOptions={[25, 50, 100]}
        component="div"
        count={sortedAndFilteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        className="meepl-bgcolor"
        sx={{
          borderTop: `1px solid #d1c4e9`,
          minHeight: '40px',
          '& .MuiTablePagination-toolbar': {
            minHeight: '40px',
          },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            marginBottom: 0,
            marginTop: 0,
          }
        }}
      />

      {/* Column Selector Popover - Reduced padding */}
      <Popover
        open={Boolean(columnSelectorAnchor)}
        anchorEl={columnSelectorAnchor}
        onClose={() => setColumnSelectorAnchor(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 1.5, minWidth: 200, maxWidth: 280, maxHeight: 400, overflow: 'auto' }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, fontSize: '13px' }}>
            Show Columns
          </Typography>
          <List dense sx={{ py: 0 }}>
            {reorderedColumns.map((column, index) => (
              <ListItem 
                key={index}
                button
                onClick={() => toggleColumnVisibility(index)}
                sx={{ 
                  py: 0.25, // Reduced padding
                  borderRadius: 0.5,
                  '&:hover': {
                    backgroundColor: 'rgba(139, 68, 255, 0.1)',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}> {/* Reduced minWidth */}
                  <Checkbox
                    checked={visibleColumns[index]}
                    size="small"
                    sx={{ 
                      p: 0.25, // Reduced padding
                    }}
                  />
                </ListItemIcon>
                <ListItemText 
                  primary={column.label} 
                  primaryTypographyProps={{ 
                    fontSize: '13px', // Slightly smaller font
                    textAlign: 'left',
                    sx: {
                      wordWrap: 'break-word',
                      whiteSpace: 'normal',
                      lineHeight: 1.3, // Tighter line height
                    }
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Popover>
    </Paper>
  );
};

export default CustomTable;